/**
 * Per-user note text highlights (DOM apply / serialize).
 * Offsets are plain-text character indices within the note body — never mutate API note HTML.
 */

export const NOTE_HIGHLIGHT_COLORS = [
  { id: 'yellow', label: 'Yellow', value: '#fff59d' },
  { id: 'green', label: 'Green', value: '#a5d6a7' },
  { id: 'blue', label: 'Blue', value: '#90caf9' },
  { id: 'pink', label: 'Pink', value: '#f48fb1' },
  { id: 'orange', label: 'Orange', value: '#ffcc80' },
];

const HIGHLIGHT_CLASS = 'note-user-highlight';

export function createHighlightId() {
  return `nh_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function isSkipNode(node, container) {
  let p = node;
  while (p && p !== container) {
    if (p.nodeType === 1) {
      const tag = p.nodeName;
      if (tag === 'SCRIPT' || tag === 'STYLE') return true;
    }
    p = p.parentNode;
  }
  return false;
}

/** Collect text nodes under container (includes text inside existing highlights). */
export function collectTextNodes(container) {
  const list = [];
  if (!container) return list;
  const walk = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
      if (isSkipNode(node, container)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n;
  while ((n = walk.nextNode())) list.push(n);
  return list;
}

export function getPlainTextLength(container) {
  return collectTextNodes(container).reduce((sum, n) => sum + (n.nodeValue || '').length, 0);
}

/** Character offset range of a DOM Range relative to container plain text. */
export function rangeToOffsets(container, range) {
  if (!container || !range) return null;
  try {
    const pre = range.cloneRange();
    pre.selectNodeContents(container);
    pre.setEnd(range.startContainer, range.startOffset);
    const start = pre.toString().length;
    const end = start + range.toString().length;
    if (end <= start) return null;
    return { start, end };
  } catch (e) {
    return null;
  }
}

export function offsetsToRange(container, start, end) {
  if (!container || start == null || end == null || end <= start) return null;
  const nodes = collectTextNodes(container);
  let pos = 0;
  let startNode = null;
  let startOffset = 0;
  let endNode = null;
  let endOffset = 0;

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    const len = (node.nodeValue || '').length;
    const next = pos + len;

    if (startNode === null && start >= pos && start <= next) {
      startNode = node;
      startOffset = start - pos;
    }
    if (endNode === null && end >= pos && end <= next) {
      endNode = node;
      endOffset = end - pos;
      break;
    }
    pos = next;
  }

  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    return range;
  } catch (e) {
    return null;
  }
}

function unwrapMark(mark) {
  const parent = mark.parentNode;
  if (!parent) return;
  while (mark.firstChild) {
    parent.insertBefore(mark.firstChild, mark);
  }
  parent.removeChild(mark);
  parent.normalize();
}

/** Unwrap all user highlight marks (keeps text). */
export function clearUserHighlights(container) {
  if (!container) return;
  container.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((m) => unwrapMark(m));
}

/**
 * Wrap each text-node slice in the range with <mark class="note-user-highlight">.
 * Works across element boundaries without breaking block structure.
 */
export function wrapRangeWithHighlight(range, color, highlightId) {
  if (!range || range.collapsed) return false;

  const root = range.commonAncestorContainer.nodeType === 3
    ? range.commonAncestorContainer.parentNode
    : range.commonAncestorContainer;
  if (!root) return false;

  const texts = [];
  const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let n;
  while ((n = walk.nextNode())) {
    if (!range.intersectsNode(n)) continue;
    if (!n.nodeValue) continue;
    texts.push(n);
  }
  if (!texts.length) return false;

  // Process from end → start so earlier offsets stay valid
  for (let i = texts.length - 1; i >= 0; i -= 1) {
    const textNode = texts[i];
    const full = textNode.nodeValue || '';
    let from = 0;
    let to = full.length;

    if (textNode === range.startContainer) from = range.startOffset;
    if (textNode === range.endContainer) to = range.endOffset;
    if (from >= to) continue;

    const before = full.slice(0, from);
    const mid = full.slice(from, to);
    const after = full.slice(to);
    if (!mid) continue;

    const mark = document.createElement('mark');
    mark.className = HIGHLIGHT_CLASS;
    mark.dataset.highlightId = highlightId;
    mark.style.backgroundColor = color;
    mark.style.color = '#000';
    mark.appendChild(document.createTextNode(mid));

    const parent = textNode.parentNode;
    if (!parent) continue;
    const frag = document.createDocumentFragment();
    if (before) frag.appendChild(document.createTextNode(before));
    frag.appendChild(mark);
    if (after) frag.appendChild(document.createTextNode(after));
    parent.replaceChild(frag, textNode);
  }
  return true;
}

export function removeHighlightById(container, highlightId) {
  if (!container || !highlightId) return;
  const id = String(highlightId);
  container.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((m) => {
    if (m.dataset && m.dataset.highlightId === id) unwrapMark(m);
  });
}

export function findHighlightIdFromNode(node, container) {
  let p = node;
  while (p && p !== container) {
    if (
      p.nodeType === 1
      && p.classList
      && p.classList.contains(HIGHLIGHT_CLASS)
      && p.dataset
      && p.dataset.highlightId
    ) {
      return p.dataset.highlightId;
    }
    p = p.parentNode;
  }
  return null;
}

/**
 * Apply saved highlight records onto a clean (API) HTML container.
 * Apply from end → start so earlier wraps don't shift later offsets.
 */
export function applyHighlightsToContainer(container, highlights) {
  if (!container || !Array.isArray(highlights) || !highlights.length) return;
  clearUserHighlights(container);

  const sorted = highlights
    .slice()
    .filter((h) => h && h.start != null && h.end != null && h.end > h.start)
    .sort((a, b) => b.start - a.start);

  sorted.forEach((h) => {
    const range = offsetsToRange(container, h.start, h.end);
    if (!range) return;
    const plain = range.toString();
    if (h.text) {
      const a = String(h.text).replace(/\s+/g, ' ').trim();
      const b = String(plain).replace(/\s+/g, ' ').trim();
      // Skip only when content clearly drifted (different length + no overlap)
      if (a && b && a !== b && !b.includes(a) && !a.includes(b) && Math.abs(a.length - b.length) > 8) {
        return;
      }
    }
    wrapRangeWithHighlight(range, h.color || '#fff59d', h.id || createHighlightId());
  });
}

/** Build a selection Range that is constrained to container (or null). */
export function getSelectionRangeInContainer(container) {
  const sel = window.getSelection && window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;
  if (!range.toString().trim()) return null;
  return range;
}
